import React, { useEffect, useState } from "react";
import axios from "axios";
import { DateTime } from "luxon";

import API_URL from "../config";
import { useIdioma } from "../context/IdiomaContext";
import { useEstilo } from "../context/EstiloContext";
import { colorTextoLegible } from "../utils/contraste";

/* =========================================
   🎨 COLORES SEGÚN ESTADO
========================================= */
const obtenerEstadoVisual = (estado, t) => {
  if (estado === "atendida") {
    return { texto: t("estado_atendida"), fondo: "#DCFCE7", color: "#16A34A" };
  }
  if (estado === "no_asistio") {
    return { texto: t("estado_no_asistio"), fondo: "#FEE2E2", color: "#DC2626" };
  }
  if (estado === "cancelada_admin") {
    return { texto: t("estado_cancelada_admin"), fondo: "#FEE2E2", color: "#B91C1C" };
  }
  if (estado === "cancelada_profesor") {
    return { texto: t("estado_cancelada_profesor"), fondo: "#FEF3C7", color: "#D97706" };
  }
  if (estado === "pendiente") {
    return { texto: t("estado_pendiente"), fondo: "#DCFCE7", color: "#16A34A" };
  }
  return { texto: t("estado_sin_estado"), fondo: "#F3F4F6", color: "#374151" };
};

const MisCitasPadre = ({ usuario, onVolver, fondoEfectivo }) => {
  const { t, idioma } = useIdioma();
  const estilo = useEstilo();
  const colorTexto = colorTextoLegible(fondoEfectivo);
  const [citas, setCitas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [paginaActual, setPaginaActual] = useState(1);
  const citasPorPagina = 5;

  const formatoFecha =
    idioma === "de" ? "dd. LLLL yyyy" : idioma === "en" ? "LLLL dd, yyyy" : "dd 'de' LLLL yyyy";

  const formatFecha = (fecha) =>
    fecha ? DateTime.fromISO(fecha).setLocale(idioma).toFormat(formatoFecha) : "—";

  const formatHora = (hora) =>
    hora ? DateTime.fromFormat(hora, "HH:mm:ss").toFormat("hh:mm a") : "—";

  useEffect(() => {
    const cargar = async () => {
      try {
        setCargando(true);
        const res = await axios.get(
          `${API_URL}/api/citas/padre/${encodeURIComponent(usuario?.email || "")}`
        );
        setCitas(res.data || []);
      } catch (err) {
        console.error("Error cargando mis citas:", err);
      } finally {
        setCargando(false);
      }
    };

    if (usuario?.email) cargar();
  }, [usuario?.email]);

  const citasFiltradas = citas
    .filter((c) => {
      const activas = c.estado === "pendiente";
      const historial =
        c.estado === "atendida" ||
        c.estado === "no_asistio" ||
        c.estado === "cancelada_admin" ||
        c.estado === "cancelada_profesor";

      // El historial solo muestra citas del año en curso — el año que viene
      // ya no aparecen las de este año.
      const esDelAnoActual = c.fecha && DateTime.fromISO(c.fecha).year === DateTime.now().year;

      return mostrarHistorial ? historial && esDelAnoActual : activas;
    })
    .sort((a, b) => {
      const claveA = `${a.fecha}${a.hora_inicio}`;
      const claveB = `${b.fecha}${b.hora_inicio}`;
      const diff = claveA.localeCompare(claveB);
      return mostrarHistorial ? -diff : diff;
    });

  const totalPaginas = Math.ceil(citasFiltradas.length / citasPorPagina);
  const citasPaginadas = citasFiltradas.slice(
    (paginaActual - 1) * citasPorPagina,
    paginaActual * citasPorPagina
  );

  return (
    <div className="w-full max-w-3xl mx-auto p-3 sm:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: colorTexto }}>{t("miscitas_titulo")}</h1>
          <p className="mt-1" style={{ color: colorTexto, opacity: 0.8 }}>{t("miscitas_subtitulo")}</p>
        </div>

        <div className="flex gap-2">
          {onVolver && (
            <button
              onClick={onVolver}
              className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-xl shadow-sm font-semibold transition"
            >
              {t("miscitas_regresar")}
            </button>
          )}
          {!estilo.misCitasTabs && (
            <button
              onClick={() => {
                setMostrarHistorial(!mostrarHistorial);
                setPaginaActual(1);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl shadow font-semibold transition"
            >
              {mostrarHistorial ? t("miscitas_ver_pendientes") : t("miscitas_ver_historial")}
            </button>
          )}
        </div>
      </div>

      {estilo.misCitasTabs && (
        <div className="flex gap-1 bg-white rounded-xl shadow-sm border border-gray-100 p-1 mb-6 w-fit">
          <button
            onClick={() => {
              setMostrarHistorial(false);
              setPaginaActual(1);
            }}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
              !mostrarHistorial ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {t("miscitas_ver_pendientes").replace("📌 ", "")}
          </button>
          <button
            onClick={() => {
              setMostrarHistorial(true);
              setPaginaActual(1);
            }}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
              mostrarHistorial ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {t("miscitas_ver_historial").replace("📚 ", "")}
          </button>
        </div>
      )}

      {cargando ? (
        <p className="text-center text-gray-500 py-10">{t("miscitas_cargando")}</p>
      ) : citasFiltradas.length === 0 ? (
        <div className="bg-white border rounded-2xl shadow p-10 text-center text-gray-500">
          {mostrarHistorial ? t("miscitas_sin_historial") : t("miscitas_sin_pendientes")}
        </div>
      ) : (
        <div className="grid gap-4">
          {citasPaginadas.map((cita) => {
            const est = obtenerEstadoVisual(cita.estado, t);
            return (
              <div
                key={cita.id}
                className="bg-white rounded-2xl shadow-md border border-gray-100 p-5"
                style={{ borderLeft: `4px solid ${est.color}` }}
              >
                <div
                  style={{ background: est.fondo, color: est.color }}
                  className="inline-block px-3 py-1 rounded-full font-bold text-xs mb-3"
                >
                  {est.texto}
                </div>

                <div className="space-y-1.5 text-gray-700 text-sm">
                  <p><span className="font-semibold">{t("miscitas_alumno")}</span> {cita.alumno_nombre}</p>
                  <p><span className="font-semibold">{t("miscitas_grado")}</span> {cita.grado}</p>
                  <p><span className="font-semibold">{t("miscitas_profesor")}</span> {cita.profesor_nombre}</p>
                  <p><span className="font-semibold">{t("miscitas_fecha")}</span> {formatFecha(cita.fecha)}</p>
                  <p><span className="font-semibold">{t("miscitas_hora")}</span> {formatHora(cita.hora_inicio)} - {formatHora(cita.hora_fin)}</p>
                </div>

                {cita.nota && (
                  <div className="bg-gray-50 border rounded-xl p-3 mt-3 text-sm">
                    <p className="font-semibold text-gray-800 mb-1">{t("miscitas_nota")}</p>
                    <p className="text-gray-600">{cita.nota}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {totalPaginas > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
          <button
            disabled={paginaActual === 1}
            onClick={() => setPaginaActual((p) => p - 1)}
            className="px-3 py-2 rounded-lg font-semibold bg-gray-200 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("miscitas_anterior")}
          </button>

          {Array.from({ length: totalPaginas }, (_, i) => (
            <button
              key={i}
              onClick={() => setPaginaActual(i + 1)}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                paginaActual === i + 1 ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={paginaActual === totalPaginas}
            onClick={() => setPaginaActual((p) => p + 1)}
            className="px-3 py-2 rounded-lg font-semibold bg-gray-200 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("miscitas_siguiente")}
          </button>
        </div>
      )}
    </div>
  );
};

export default MisCitasPadre;
